// @ts-nocheck
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFeaturedChannels } from '../hooks/useFeaturedChannels';
import { usePairDevice } from '../hooks/usePairDevice';
import { useSetDeviceQueue } from '../hooks/useSetDeviceQueue';
import ShimmerButton from './ShimmerButton';
import MobileArtcastCard from './MobileArtcastCard';
import { AlreadyPairedModal } from './AlreadyPairedModal';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAnalyticsOnce } from '../hooks/useAnalyticsOnce';
import {
    trackErrorWithContext,
    getSessionContext,
    AnalyticsEvents,
} from '../utils/analytics';
import { api } from '../services/api';

interface ArtcastSelectionProps {
    userEmail: string;
    userId: string;
    deviceId: string;
    deviceCode: string;
    onSuccess: (queueData?: any) => void;
    onSelectionChange?: (selectedChannelId: string | null) => void;
    onBackToStart?: () => void;
    savedQueueData?: any;
    isViewingSavedQueue?: boolean;
    onStartFresh?: () => void;
    aiSearchedChannels?: any[] | null;
    giftChannels?: any[] | null;
    isAIModalOpen?: boolean; // Add this to prevent fetching when AI modal is open
}

export default function ArtcastSelection({
    userEmail,
    userId,
    deviceId,
    deviceCode,
    onSuccess,
    onSelectionChange,
    onBackToStart,
    savedQueueData,
    isViewingSavedQueue,
    onStartFresh,
    aiSearchedChannels,
    giftChannels,
    isAIModalOpen,
}: ArtcastSelectionProps) {
    const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
    const analytics = useAnalytics();
    const { captureOnce } = useAnalyticsOnce();
    const [isProcessing, setIsProcessing] = useState(false);
    const [previewingChannel, setPreviewingChannel] = useState<string | null>(
        null
    );
    const [expandedChannel, setExpandedChannel] = useState<string | null>(null);
    const [showAlreadyPairedModal, setShowAlreadyPairedModal] = useState(false);
    const [showStickyButton, setShowStickyButton] = useState(false);
    const savedScrollPosition = useRef<number>(0);
    const lastAttemptedQueueData = useRef<any>(null);
    const isSnapping = useRef(false);
    const snapTimeout = useRef<NodeJS.Timeout>();
    const lastScrollY = useRef<number>(0);
    const scrollVelocity = useRef<number>(0);
    const lastScrollTime = useRef<number>(Date.now());
    const lastLockedCard = useRef<string | null>(null);
    const snapTargetY = useRef<number | null>(null);
    const userInterrupted = useRef(false);
    const programmaticScrolling = useRef(false);
    const lastSnapTime = useRef<number>(0);

    // Check for mock hooks in test environment
    const mockHooks =
        typeof window !== 'undefined' ? (window as any).__MOCK_HOOKS__ : null;

    // Only load featured channels if we're not viewing a saved queue, no AI search, and AI modal is not open
    const shouldUseFeatured =
        !isViewingSavedQueue &&
        !aiSearchedChannels &&
        !isAIModalOpen &&
        !savedQueueData;

    // Debug logging
    console.log('[PAIR-APP] ArtcastSelection render state:', {
        isViewingSavedQueue,
        hasAiSearchedChannels: !!aiSearchedChannels,
        hasSavedQueueData: !!savedQueueData,
        isAIModalOpen,
        shouldUseFeatured,
    });

    // Always call the hook unconditionally (React rules of hooks)
    // Pass the enabled flag to prevent unnecessary fetching
    const featuredChannelsResult =
        mockHooks?.useFeaturedChannels?.() ||
        useFeaturedChannels(shouldUseFeatured);

    // Then conditionally use the result
    const { data: fetchedChannels, isLoading } = shouldUseFeatured
        ? featuredChannelsResult
        : { data: null, isLoading: false };

    // Initialize channels variable
    let channels;

    // PRIORITY 1: If viewing saved queue, use ONLY the saved queue data
    if (isViewingSavedQueue && savedQueueData) {
        console.log(
            '[PAIR-APP] Using ONLY saved queue data - no additional channels'
        );
        const queueData =
            savedQueueData.queue?.[0] || savedQueueData[0] || savedQueueData;
        if (queueData?.channelData) {
            channels = [queueData.channelData];
        } else if (queueData?.section === 'channel' && queueData?.channelID) {
            channels = [
                {
                    id: queueData.channelID,
                    name: 'Saved Channel',
                    artworks: queueData.artworks || [],
                },
            ];
        }
    } else {
        // PRIORITY 2: Otherwise use AI searched or featured channels
        channels = aiSearchedChannels || fetchedChannels;
    }

    // Only add gift channels if NOT viewing saved queue
    if (!isViewingSavedQueue && giftChannels && giftChannels.length > 0) {
        console.log(
            '[PAIR-APP] Gift channels detected, adding to channel list:',
            giftChannels
        );

        // If we already have channels, combine them with gift channels first
        if (channels && channels.length > 0) {
            // Filter out any duplicate channels (in case gift channel is already in the list)
            const giftChannelIds = new Set(
                giftChannels.map((ch) => ch.id || ch.channelID)
            );
            const otherChannels = channels.filter(
                (ch) => !giftChannelIds.has(ch.id || ch.channelID)
            );
            channels = [...giftChannels, ...otherChannels];
        } else {
            // If no other channels, just use gift channels
            channels = giftChannels;
        }

        // Debug: Log gift channels with their isGift property
        console.log(
            '[PAIR-APP] Gift channels after combination:',
            channels
                .filter((ch) => ch.isGift)
                .map((ch) => ({
                    id: ch.id,
                    name: ch.name,
                    isGift: ch.isGift,
                }))
        );
    }

    // Debug logging for channels
    if (channels) {
        console.log('🎨 ArtcastSelection - Channels received:', channels);
        channels.forEach((channel: any) => {
            console.log(`  🖼️ Channel "${channel.name}":`, {
                id: channel.id,
                artworksCount: channel.artworks?.length || 0,
                artworks: channel.artworks,
                hasCollections: !!channel.collections?.length,
                collections: channel.collections,
            });
        });
    }

    // Track (non-blocking) channel browsing started when channels are loaded
    // Use captureOnce to prevent duplicate events
    useEffect(() => {
        if (channels && channels.length > 0 && !isLoading) {
            captureOnce(
                AnalyticsEvents.ARTCAST_BROWSING_STARTED,
                {
                    user_id: userId,
                    channel_count: channels.length,
                    is_ai_searched: !!aiSearchedChannels,
                    is_saved_queue: !!isViewingSavedQueue,
                    has_device_params: !!(deviceId && deviceCode),
                    ...getSessionContext(),
                },
                'artcast_browsing_started_session' // Unique key for this session
            );
        }
    }, [
        // Only depend on whether channels exist and loading state
        // This prevents re-firing when other props change
        !!channels && channels.length > 0,
        isLoading,
        captureOnce,
        userId,
    ]);

    const pairDevice = mockHooks?.usePairDevice?.() || usePairDevice();
    const setDeviceQueue =
        mockHooks?.useSetDeviceQueue?.() || useSetDeviceQueue();

    // Effect to restore scroll position when unselecting
    useEffect(() => {
        if (!selectedChannel && savedScrollPosition.current > 0) {
            // Immediately restore the scroll position without animation
            window.scrollTo({
                top: savedScrollPosition.current,
                behavior: 'instant',
            });
            // Clear the saved position
            savedScrollPosition.current = 0;
        }
    }, [selectedChannel]);

    // Scroll to top when AI searched channels are loaded
    useEffect(() => {
        if (aiSearchedChannels && aiSearchedChannels.length > 0) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, [aiSearchedChannels]);

    // Disable JavaScript-based snap-to scroll - using CSS scroll-snap instead
    useEffect(() => {
        return; // Disable this entire effect

        if (selectedChannel) return;

        const handleScrollEnd = () => {
            if (isSnapping.current) return;

            // Don't snap if we're at the top or bottom (elastic bounce)
            const scrollY = window.scrollY;
            const maxScroll =
                document.documentElement.scrollHeight - window.innerHeight;

            if (scrollY <= 0 || scrollY >= maxScroll) {
                console.log('🔒 At scroll boundary, skipping snap');
                return;
            }

            // Prevent rapid re-snapping
            const now = Date.now();
            if (now - lastSnapTime.current < 500) {
                console.log('⏱️ Too soon since last snap, skipping');
                return;
            }

            const cards = document.querySelectorAll('.artcast-card-container');
            const viewportCenter = window.innerHeight / 2;
            let targetCard: HTMLElement | null = null;
            let closestCard: HTMLElement | null = null;
            let closestCardId: string | null = null;
            let minDistance = Infinity;

            // Find the closest card to viewport center
            cards.forEach((card, index) => {
                const rect = card.getBoundingClientRect();
                const cardCenter = rect.top + rect.height / 2;
                const distance = Math.abs(cardCenter - viewportCenter);

                // Keep track of closest card
                if (distance < minDistance) {
                    minDistance = distance;
                    closestCard = card as HTMLElement;
                    closestCardId = `card-${index}`;
                }
            });

            // Don't snap if user is still scrolling fast (let momentum play out)
            if (scrollVelocity.current > 8) {
                console.log('⚡ High velocity scroll, skipping snap');
                return;
            }

            // Determine snap threshold based on whether we're already locked
            const baseThreshold = 50; // Standard threshold
            // If we're locked on this card, make it easier to escape (lower threshold)
            const snapThreshold =
                lastLockedCard.current === closestCardId
                    ? baseThreshold * 0.5
                    : baseThreshold;

            // Only snap if we're beyond the threshold
            if (closestCard && minDistance > snapThreshold) {
                targetCard = closestCard;
                lastLockedCard.current = closestCardId;

                isSnapping.current = true;
                userInterrupted.current = false;
                programmaticScrolling.current = true;
                lastSnapTime.current = Date.now();
                const rect = targetCard.getBoundingClientRect();
                const scrollTop =
                    window.pageYOffset || document.documentElement.scrollTop;
                const targetPosition = scrollTop + rect.top - 120;
                snapTargetY.current = targetPosition;

                console.log(
                    '📍 Snapping to card (velocity:',
                    scrollVelocity.current.toFixed(2),
                    ')'
                );

                // Start the smooth scroll
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth',
                });

                // Monitor for user interruption during snap animation
                const checkInterval = setInterval(() => {
                    if (userInterrupted.current || !isSnapping.current) {
                        // User started scrolling - stop everything
                        clearInterval(checkInterval);
                        isSnapping.current = false;
                        snapTargetY.current = null;
                        // Cancel the smooth scroll by setting position to current
                        window.scrollTo({
                            top: window.scrollY,
                            behavior: 'auto',
                        });
                        console.log('🛑 Snap cancelled by user scroll');
                        return;
                    }

                    // Check if we've reached the target
                    if (Math.abs(window.scrollY - targetPosition) < 5) {
                        clearInterval(checkInterval);
                        isSnapping.current = false;
                        snapTargetY.current = null;
                        programmaticScrolling.current = false;
                    }
                }, 16); // ~60fps checking

                // Failsafe timeout
                setTimeout(() => {
                    clearInterval(checkInterval);
                    isSnapping.current = false;
                    snapTargetY.current = null;
                    programmaticScrolling.current = false;
                }, 500);
            }
        };

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const currentTime = Date.now();
            const timeDiff = currentTime - lastScrollTime.current;
            const scrollDiff = Math.abs(currentScrollY - lastScrollY.current);

            // Calculate velocity (pixels per millisecond)
            if (timeDiff > 0) {
                scrollVelocity.current = scrollDiff / timeDiff;
            }

            // If we're currently snapping and user scrolls, interrupt it
            // But ignore if it's our own programmatic scroll
            if (
                isSnapping.current &&
                scrollDiff > 2 &&
                !programmaticScrolling.current
            ) {
                // Check if scroll is away from snap target (user is overriding)
                if (snapTargetY.current !== null) {
                    const scrollDirection =
                        currentScrollY > lastScrollY.current ? 1 : -1;
                    const targetDirection =
                        snapTargetY.current > lastScrollY.current ? 1 : -1;

                    // If user is scrolling away from target or has meaningful velocity
                    if (
                        scrollDirection !== targetDirection ||
                        scrollVelocity.current > 0.5
                    ) {
                        userInterrupted.current = true;
                        isSnapping.current = false;
                        programmaticScrolling.current = false;
                        console.log('🚫 User interrupted snap animation');
                    }
                }
            }

            // After a small delay, allow interruption detection
            // This prevents the initial programmatic scroll from being detected
            if (programmaticScrolling.current && timeDiff > 100) {
                programmaticScrolling.current = false;
            }

            // If user is scrolling with any meaningful velocity, clear the locked card
            // This makes it easier to escape from a snapped position
            if (scrollVelocity.current > 0.5 && scrollDiff > 10) {
                lastLockedCard.current = null;
            }

            lastScrollY.current = currentScrollY;
            lastScrollTime.current = currentTime;

            // Clear previous timeout
            if (snapTimeout.current) {
                clearTimeout(snapTimeout.current);
            }

            // Don't set new timeout if we're already snapping (unless interrupted)
            if (isSnapping.current && !userInterrupted.current) {
                return;
            }

            // For fast scrolling, wait longer to let momentum play out
            // For slow scrolling, snap quickly
            const timeout =
                scrollVelocity.current > 10
                    ? 300
                    : scrollVelocity.current > 5
                    ? 150
                    : 50;
            snapTimeout.current = setTimeout(handleScrollEnd, timeout);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (snapTimeout.current) {
                clearTimeout(snapTimeout.current);
            }
        };
    }, [selectedChannel]);

    // Add scroll detection for sticky button
    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const isMobile = window.innerWidth < 768;

            // Check if page has scrollable content
            const hasScroll =
                document.documentElement.scrollHeight > window.innerHeight;

            if (isMobile) {
                // On mobile: show after scrolling 50px
                setShowStickyButton(scrollY > 50);
            } else {
                // On desktop: show immediately if no scroll possible, or after minimal scrolling
                setShowStickyButton(!hasScroll || scrollY > 20);
            }
        };

        // Check initial state
        handleScroll();

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);

    const handleSelection = async (channelId?: string) => {
        const channelToUse = channelId || selectedChannel;
        if (!channelToUse) return;

        setIsProcessing(true);
        console.log('[PAIR-APP] Starting device pairing process...');

        // Store the channel data for later use (in case of already paired error)
        const selectedChannelData = channels?.find(
            (c) => c.id === channelToUse
        );

        // Track (non-blocking) artcast selection when user proceeds to pair
        // This handles both "Play On Screen" clicks and regular selection flow
        if (channelId && selectedChannelData) {
            // Get first 3 artwork details for analytics
            const artworkDetails =
                selectedChannelData.artworks
                    ?.slice(0, 3)
                    .map((artwork: any) => ({
                        id: artwork.id,
                        title: artwork.title,
                        artist: artwork.artist,
                        type: artwork.type || 'unknown',
                    })) || [];

            analytics.trackEvent(AnalyticsEvents.ARTCAST_SELECTED, {
                channelId: selectedChannelData.id,
                channelName: selectedChannelData.name,
                artworkCount: selectedChannelData.artworks?.length || 0,
                artworkDetails: artworkDetails,
                firstArtworkTitle: artworkDetails[0]?.title || null,
                firstArtworkArtist: artworkDetails[0]?.artist || null,
                selection_method: 'play_on_screen',
                is_ai_curated: !!aiSearchedChannels,
                is_saved_queue: !!isViewingSavedQueue,
                ...getSessionContext(),
            });
        }

        // Prepare queue data based on whether we're using saved queue or not
        let queueData;
        if (isViewingSavedQueue && savedQueueData) {
            // Extract the queue data from the saved queue structure
            const savedQueue =
                savedQueueData.queue?.[0] ||
                savedQueueData[0] ||
                savedQueueData;
            queueData = {
                channelId: savedQueue.channelID || channelToUse,
                channelData: savedQueue.channelData || selectedChannelData,
                artworks: savedQueue.artworks || [],
            };
        } else {
            // Normal queue data from selected channel
            queueData = {
                channelId: channelToUse,
                channelData: selectedChannelData,
                artworks: selectedChannelData?.artworks || [],
            };
        }

        // Always include gift channels if they exist
        if (giftChannels && giftChannels.length > 0) {
            console.log(
                '[PAIR-APP] Including gift channels in queue data:',
                giftChannels
            );

            // If user selected a gift channel, only include that gift channel
            const selectedGiftChannel = giftChannels.find(
                (gc) => gc.id === channelToUse || gc.channelID === channelToUse
            );

            if (selectedGiftChannel) {
                // User selected the gift channel itself, queue data is already correct
                queueData.isGift = true;
            } else {
                // User selected a different channel, include gift channels as additional subscriptions
                queueData.additionalChannels = giftChannels.map((gc) => ({
                    channelId: gc.id || gc.channelID,
                    channelData: gc,
                    artworks: gc.artworks || [],
                    isGift: true,
                }));

                console.log(
                    '[PAIR-APP] Added gift channels as additional subscriptions:',
                    queueData.additionalChannels
                );
            }
        }

        // Store it in ref for later use
        lastAttemptedQueueData.current = queueData;

        try {
            // Only try to pair if we have device params
            if (deviceId && deviceCode) {
                // First pair the device
                console.log(
                    '[PAIR-APP] Step 1: Calling pairDevice.mutateAsync...'
                );
                const pairResult = await pairDevice.mutateAsync({
                    userId,
                    deviceId,
                    deviceCode,
                });
                console.log(
                    '[PAIR-APP] Step 1 Complete: Device paired successfully',
                    pairResult
                );

                // Then set the device queue with selected channel
                console.log('[PAIR-APP] Step 2: Setting device queue...');

                // Prepare artworks array based on the source
                let artworksForQueue;
                if (isViewingSavedQueue && queueData.artworks?.[0]?.artworkID) {
                    // Saved queue already has artworkID structure, need to transform back to regular artwork objects
                    console.log(
                        '[PAIR-APP] Converting saved queue artworks format'
                    );
                    artworksForQueue = queueData.artworks.map((item: any) => ({
                        id: item.artworkID,
                        // Include other properties if needed
                    }));
                } else {
                    // Regular channel artworks
                    console.log('[PAIR-APP] Using regular channel artworks');
                    artworksForQueue = queueData.artworks || [];
                }

                console.log('[PAIR-APP] Artworks for queue:', artworksForQueue);

                // Use the queue data we prepared earlier
                const queueResult = await setDeviceQueue.mutateAsync({
                    userId,
                    deviceId,
                    channelId: queueData.channelId,
                    artworks: artworksForQueue,
                });
                console.log(
                    '[PAIR-APP] Step 2 Complete: Queue set successfully',
                    queueResult
                );

                console.log(
                    '[PAIR-APP] All steps completed successfully, calling onSuccess()'
                );

                // Pass the queue data when calling onSuccess
                onSuccess(queueData);
            } else {
                // No device params, just pass the queue data to go to pair screen
                console.log(
                    '[PAIR-APP] No device params, proceeding to pair screen with queue data'
                );
                onSuccess(queueData);
            }
        } catch (error: any) {
            console.error(
                '[PAIR-APP] ArtcastSelection: Error caught in handleSelection:',
                error
            );
            console.error('[PAIR-APP] Error details:', {
                errorType: error?.errorType,
                error: error?.error,
                status: error?.status,
                __isApiError: error?.__isApiError,
                fullError: error,
            });
            setIsProcessing(false);

            // Check if it's a device already paired error
            if (error && error.errorType === 'device_already_paired') {
                console.log('[PAIR-APP] Device already paired, showing modal');
                // Clear device params from URL
                const url = new URL(window.location.href);
                url.searchParams.delete('deviceId');
                url.searchParams.delete('deviceCode');
                url.searchParams.delete('shortSecret');
                url.searchParams.delete('deviceType');
                window.history.replaceState({}, '', url.toString());
                setShowAlreadyPairedModal(true);
            }
            // Also check for the specific error message when device is already assigned
            else if (
                error &&
                error.error &&
                typeof error.error === 'string' &&
                error.error.includes('Device is already assigned to a user')
            ) {
                console.log(
                    '[PAIR-APP] Device already assigned to a user, showing modal'
                );
                // Clear device params from URL
                const url = new URL(window.location.href);
                url.searchParams.delete('deviceId');
                url.searchParams.delete('deviceCode');
                url.searchParams.delete('shortSecret');
                url.searchParams.delete('deviceType');
                window.history.replaceState({}, '', url.toString());
                setShowAlreadyPairedModal(true);
            }
            // For any other error, don't proceed to success
            else {
                console.error(
                    '[PAIR-APP] Pairing failed, not proceeding to success screen'
                );
                console.error(
                    '[PAIR-APP] onSuccess() will NOT be called due to error'
                );

                // Clear device params from URL when pairing fails
                console.log(
                    '[PAIR-APP] Clearing device params from URL after pairing failure'
                );
                const url = new URL(window.location.href);
                url.searchParams.delete('deviceId');
                url.searchParams.delete('deviceCode');
                url.searchParams.delete('shortSecret');
                url.searchParams.delete('deviceType');
                window.history.replaceState({}, '', url.toString());

                // Navigate to pair screen so user can scan QR code
                // Pass the queue data but with a failure flag
                const failureQueueData = {
                    ...queueData,
                    pairingFailed: true,
                    errorMessage: error?.error || 'Failed to pair device',
                };
                onSuccess(failureQueueData);
            }
        }
    };

    if (isLoading) {
        return (
            <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                    <motion.div
                        key={i}
                        className="h-24 rounded-xl bg-gray-800"
                        data-testid="loading-skeleton"
                        animate={{ opacity: [0.5, 0.8, 0.5] }}
                        transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            delay: i * 0.2,
                        }}
                    />
                ))}
            </div>
        );
    }

    return (
        <>
            {channels?.map((channel, index) => {
                // Hide non-selected artcasts when one is selected
                const isHidden =
                    selectedChannel && selectedChannel !== channel.id;

                return (
                    <div
                        key={channel.id}
                        data-index={index}
                        data-channel-id={channel.id}
                    >
                        <MobileArtcastCard
                            channel={channel}
                            isSelected={selectedChannel === channel.id}
                            isExpanded={expandedChannel === channel.id}
                            hasDeviceParams={!!(deviceId && deviceCode)}
                            isGift={!!channel.isGift}
                            onSelect={async () => {
                                // Toggle selection: if already selected, unselect it
                                if (selectedChannel === channel.id) {
                                    console.log(
                                        '🔄 Toggling off selection for:',
                                        channel.name
                                    );
                                    setSelectedChannel(null);
                                    onSelectionChange?.(null);
                                    analytics.trackEvent(
                                        AnalyticsEvents.ARTCAST_PREVIEWED,
                                        {
                                            channelId: channel.id,
                                            channelName: channel.name,
                                        }
                                    );
                                } else {
                                    console.log(
                                        '🔄 Toggling on selection for:',
                                        channel.name
                                    );
                                    // Save current scroll position before selecting
                                    savedScrollPosition.current =
                                        window.scrollY;
                                    setSelectedChannel(channel.id);
                                    onSelectionChange?.(channel.id);
                                    // Close any expanded channel when selecting a new one
                                    if (expandedChannel) {
                                        setExpandedChannel(null);
                                    }
                                    // Get first 3 artwork details for analytics
                                    const artworkDetails =
                                        channel.artworks
                                            ?.slice(0, 3)
                                            .map((artwork: any) => ({
                                                id: artwork.id,
                                                title: artwork.title,
                                                artist: artwork.artist,
                                                type: artwork.type || 'unknown',
                                            })) || [];

                                    analytics.trackEvent(
                                        AnalyticsEvents.ARTCAST_SELECTED,
                                        {
                                            channelId: channel.id,
                                            channelName: channel.name,
                                            artworkCount:
                                                channel.artworks?.length || 0,
                                            artworkDetails: artworkDetails,
                                            firstArtworkTitle:
                                                artworkDetails[0]?.title ||
                                                null,
                                            firstArtworkArtist:
                                                artworkDetails[0]?.artist ||
                                                null,
                                            selection_method:
                                                'toggle_selection',
                                            is_ai_curated: !!aiSearchedChannels,
                                            is_saved_queue:
                                                !!isViewingSavedQueue,
                                            ...getSessionContext(),
                                        }
                                    );
                                }
                            }}
                            onPlayOnScreen={() => {
                                // Directly pair and send to queue without selecting
                                console.log(
                                    '[PAIR-APP] Play On Screen clicked for:',
                                    channel.name
                                );
                                // Pass the channel ID directly to handleSelection
                                handleSelection(channel.id);
                            }}
                            isPreviewingGlobally={!!previewingChannel}
                            onPreviewChange={(isPreviewing) => {
                                if (isPreviewing) {
                                    setPreviewingChannel(channel.id);
                                    // Don't close expanded channel when starting preview - let user control it
                                } else if (previewingChannel === channel.id) {
                                    setPreviewingChannel(null);
                                }
                            }}
                            onExpansionChange={(isExpanded, channelId) => {
                                if (isExpanded) {
                                    setExpandedChannel(channelId);
                                } else if (expandedChannel === channelId) {
                                    setExpandedChannel(null);
                                }
                            }}
                            initialAnimation={{
                                opacity: 0,
                                y: 20,
                            }}
                            animateAnimation={{
                                opacity: isHidden ? 0 : 1,
                                y: isHidden ? -20 : 0,
                            }}
                            transitionAnimation={{
                                delay: isHidden ? 0 : index * 0.15,
                                duration: 0.3,
                            }}
                            isHidden={isHidden}
                        />
                    </div>
                );
            })}

            {/* Add padding at bottom for sticky button */}
            <div className="h-20 md:h-32"></div>

            {/* Sticky AI Curation Button */}
            {onStartFresh && (
                <AnimatePresence>
                    {showStickyButton && (
                        <motion.div
                            initial={{ opacity: 0, y: 100 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 100 }}
                            transition={{
                                type: 'spring',
                                stiffness: 300,
                                damping: 30,
                            }}
                            className="fixed bottom-0 left-0 right-0 z-40 md:flex md:justify-center md:pb-8"
                        >
                            <div className="w-full md:px-6 md:max-w-md md:mx-auto">
                                <button
                                    onClick={onStartFresh}
                                    className="relative w-full bg-gradient-to-b from-gray-800 via-gray-850 to-gray-900 shadow-[0_6px_30px_rgba(168,85,247,0.3)] border-t md:border border-purple-500/30 font-semibold py-4 px-6 md:rounded-xl flex items-center justify-center gap-3 overflow-hidden transition-all duration-300 group hover:shadow-[0_8px_40px_rgba(168,85,247,0.5)] hover:scale-[1.02] hover:border-purple-400/50"
                                >
                                    {/* 3D highlight effect - matching Choose Art button */}
                                    <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />

                                    {/* Additional glow effect for depth */}
                                    <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-purple-500/10 to-purple-500/5 opacity-50 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                                    {/* Purple Sparkle Icon without circle */}
                                    <span
                                        className="text-xl"
                                        style={{
                                            filter: 'hue-rotate(260deg) saturate(2) brightness(1.2)',
                                        }}
                                    >
                                        ✨
                                    </span>

                                    <span className="text-lg tracking-wide bg-gradient-to-b from-purple-300 via-purple-400 to-pink-400 bg-clip-text text-transparent font-semibold">
                                        AI CURATION
                                    </span>
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            )}

            {/* Already Paired Modal */}
            <AlreadyPairedModal
                isOpen={showAlreadyPairedModal}
                deviceId={deviceId}
                onBackToStart={() => {
                    console.log(
                        '[PAIR-APP] User requested to go back to step 1'
                    );
                    setShowAlreadyPairedModal(false);
                    // Reset local state
                    setSelectedChannel(null);
                    setIsProcessing(false);
                    // Call parent's onBackToStart if provided
                    onBackToStart?.();
                }}
                onPairNewDevice={() => {
                    console.log('[PAIR-APP] User chose to pair a new device');
                    setShowAlreadyPairedModal(false);
                    // Reset local state
                    setSelectedChannel(null);
                    setIsProcessing(false);

                    // Use the stored queue data from the last attempt
                    if (lastAttemptedQueueData.current) {
                        const queueData = {
                            ...lastAttemptedQueueData.current,
                            pairingFailed: true,
                            fromAlreadyPaired: true,
                        };
                        onSuccess(queueData);
                    } else {
                        console.error(
                            '[PAIR-APP] No queue data available for pairing new device'
                        );
                    }
                }}
            />
        </>
    );
}
